// De inlog: een magic link van Supabase, en verder niets van onszelf.
//
// Besluit H2 (W1c): toegang is een persoonlijke inlog via Supabase magic link,
// met de allowlist in ONZE gebruikerstabel. Dit bestand doet daarom precies
// twee dingen — een link laten sturen, en het token leveren dat daarna
// meereist. Wie je bent bewijst het token; wát je mag beslist de service
// (service/src/sof/auth/deps.py), nooit dit bestand.
//
// Waarom niet zelf tokens uitgeven: dan zouden we uitgifte, verversing én
// intrekking zelf moeten onderhouden. Zie de kop van sof/auth/verify.py.

import { createClient, type SupabaseClient } from "@supabase/supabase-js";

import { zetTokenBron } from "./api";

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const publiekeSleutel = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

/**
 * Is de inlog ingericht? Ontbreekt een van de twee waarden, dan draait dit
 * dashboard tegen een dienst in ontwikkelmodus (SOF_DEV_MODE/SOF_DEV_USER).
 * Dan is er geen inlog, en hoort het scherm dat te zeggen in plaats van een
 * knop te tonen die niets doet — stilte is de fout, niet de lege waarde.
 */
export const authIngericht = Boolean(url && publiekeSleutel);

const client: SupabaseClient | null = authIngericht
  ? createClient(url as string, publiekeSleutel as string, {
      auth: {
        // De sessie moet een verversing overleven; anders is elke F5 een
        // nieuwe magic link in je mailbox.
        persistSession: true,
        autoRefreshToken: true,
        // Supabase hangt het token in de hash van de terugkeer-URL. Deze regel
        // laat de client hem er zelf uithalen en de adresbalk daarna opruimen.
        detectSessionInUrl: true,
      },
    })
  : null;

export class InlogFout extends Error {}

/** Het huidige toegangstoken, of null als er geen sessie is. */
export async function huidigToken(): Promise<string | null> {
  if (!client) return null;
  const { data } = await client.auth.getSession();
  return data.session?.access_token ?? null;
}

// Vanaf hier reist het token mee met élke API-aanroep. Eén regel, op de enige
// plek in dit dashboard die het netwerk aanraakt.
zetTokenBron(huidigToken);

/**
 * Stuurt de magic link. Gelukt betekent "de mail is verstuurd", niet "je mag
 * naar binnen": dat laatste beslist de allowlist pas ná het klikken.
 *
 * `shouldCreateUser: false` (pilot 26-10, baan 1D). Vroeger bleef dit open,
 * omdat uitnodigen alleen een rij in onze tabel was en het Supabase-account pas
 * bij de eerste inlog ontstond (sessie-3 §6). Sinds de uitnodiging zelf de
 * Supabase-mail stuurt (sof/auth/uitnodiging.py), bestaat het account al vóór
 * de eerste inlog. Een onbekend adres krijgt nu geen account meer, maar de
 * Nederlandse weigering hieronder. De allowlist blijft ONZE tabel: wie wel een
 * Supabase-account heeft maar niet (meer) op de lijst staat, krijgt de 403 van
 * de service.
 */
export async function stuurMagicLink(email: string): Promise<void> {
  if (!client) throw new InlogFout("De inlog is niet ingericht op deze omgeving.");
  const { error } = await client.auth.signInWithOtp({
    email: email.trim().toLowerCase(),
    options: { emailRedirectTo: window.location.origin, shouldCreateUser: false },
  });
  if (error) throw new InlogFout(leesbaar(error.message, (error as { code?: string }).code));
}

export async function uitloggen(): Promise<void> {
  await client?.auth.signOut();
}

/**
 * Roept `bij` aan zodra de sessie verandert: inloggen, uitloggen, verversen.
 *
 * Dit is niet cosmetisch. Bij terugkeer van de magic link leest de client het
 * token uit de URL-hash, en dat gebeurt ná de eerste render — zonder deze
 * melding zou het scherm blijven staan op "je bent niet ingelogd" terwijl je
 * dat net wél bent.
 */
export function opAuthWissel(bij: () => void): () => void {
  if (!client) return () => {};
  const { data } = client.auth.onAuthStateChange(() => bij());
  return () => data.subscription.unsubscribe();
}

/** Wat een adres zonder uitnodiging te lezen krijgt. */
export const GEEN_UITNODIGING = "Dit adres heeft geen toegang. Vraag de beheerder om een uitnodiging.";

/** De Supabase-melding in gewone taal; onbekende meldingen blijven staan. */
export function leesbaar(melding: string, code?: string): string {
  const m = `${code ?? ""} ${melding}`.toLowerCase();
  // shouldCreateUser:false: Supabase weigert een adres zonder account met
  // "Signups not allowed for otp" (code otp_disabled / signup_disabled), of
  // als zelf aanmelden in het dashboard uit staat met "Signups not allowed".
  if (
    m.includes("signups not allowed") ||
    m.includes("signup_disabled") ||
    m.includes("otp_disabled") ||
    m.includes("user not found")
  ) {
    return GEEN_UITNODIGING;
  }
  if (m.includes("rate limit") || m.includes("too many")) {
    return "Er zijn net te veel links aangevraagd. Wacht een minuut en probeer opnieuw.";
  }
  if (m.includes("invalid") && m.includes("email")) {
    return "Dat lijkt geen geldig e-mailadres.";
  }
  return melding;
}
